import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { LoginForm } from '@/components/auth/LoginForm';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { getPostAuthRedirect, buildAuthSwitchUrl } from '@/lib/auth-intent';

export function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, isAuthenticated } = useAuth();
  const [error, setError] = useState<string>();
  const [testLoading, setTestLoading] = useState(false);

  const redirect = getPostAuthRedirect(searchParams);

  useEffect(() => {
    if (isAuthenticated) {
      navigate(redirect, { replace: true });
    }
  }, [isAuthenticated, navigate, redirect]);

  const handleSubmit = async (data: { email: string; password: string; rememberMe?: boolean }) => {
    try {
      await login(data);
      navigate(redirect, { replace: true });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Invalid email or password');
    }
  };

  const handleTestLogin = async () => {
    setTestLoading(true);
    setError(undefined);
    try {
      await login({ email: 'admin@acmecorp.com', password: 'password123' });
      navigate(redirect, { replace: true });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Test login failed');
    } finally {
      setTestLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Brand pane — hidden on mobile, visible lg+ */}
      <div className="hidden lg:flex lg:w-1/2 flex-col justify-between p-16 bg-slate-900 relative overflow-hidden">
        {/* Ambient radial gradient */}
        <div aria-hidden className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,hsl(221.2_83.2%_53.3%/0.25),transparent_60%)] pointer-events-none" />
        {/* Top: logo */}
        <div className="relative z-10">
          <img src="/logo-dark.png" alt="Gateco" className="h-20 w-auto" />
        </div>
        {/* Middle: headline + trust points */}
        <div className="relative z-10 space-y-10">
          <div className="space-y-4">
            <h1 className="text-5xl font-semibold tracking-tight leading-tight text-white">Gate your AI&apos;s<br/>access to knowledge.</h1>
            <p className="text-slate-400 max-w-sm text-base leading-relaxed">Policy-aware retrieval for AI systems. Every query checked, every decision logged.</p>
          </div>
          <ul className="space-y-4 text-base text-slate-400">
            {['Deny-by-default retrieval enforcement', 'Identity-based access with 4 IDP adapters', 'Full audit trail, every retrieval logged'].map((item) => (
              <li key={item} className="flex items-center gap-3">
                <span className="h-2 w-2 rounded-full bg-blue-400 shrink-0" />
                {item}
              </li>
            ))}
          </ul>
        </div>
        {/* Bottom: trust signal */}
        <p className="relative z-10 text-sm text-slate-500">SOC 2 Type II in progress · GDPR-ready</p>
      </div>

      {/* Form pane */}
      <div className="flex w-full lg:w-1/2 flex-col items-center justify-center p-10 bg-background">
        <div className="w-full max-w-lg space-y-8">
          {/* Mobile-only logo */}
          <div className="lg:hidden text-center">
            <Link to="/"><img src="/logo.png" alt="Gateco" className="h-12 w-auto mx-auto" /></Link>
          </div>
          <div>
            <h2 className="text-3xl font-semibold tracking-tight">Sign in</h2>
            <p className="text-base text-muted-foreground mt-2">Enter your credentials to continue</p>
          </div>
          <div className="rounded-xl border border-border bg-card p-10 shadow-sm">
            <LoginForm onSubmit={handleSubmit} error={error} onClearError={() => setError(undefined)} />
          </div>
          <p className="text-center text-base text-muted-foreground">
            Don&apos;t have an account?{' '}
            <Link to={buildAuthSwitchUrl('signup', searchParams)} className="text-primary hover:underline">Sign up</Link>
          </p>
          {import.meta.env.DEV && (
            <div>
              <Button variant="outline" className="w-full h-11 text-base" onClick={handleTestLogin} disabled={testLoading}>
                {testLoading ? 'Signing in…' : 'Dev: Login as test user'}
              </Button>
              <p className="text-sm text-center text-muted-foreground mt-2">admin@acmecorp.com · password123</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
